import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Building2, CheckCircle, Clock, XCircle, Plus, Search } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { prestacoesApi, unidadesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { PrestacaoContas, Unidade } from '../types';

export function PrestacoesPage() {
  const [prestacoes, setPrestacoes] = useState<PrestacaoContas[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroUnidade, setFiltroUnidade] = useState<number | undefined>();
  const [filtroBusca, setFiltroBusca] = useState('');
  const { selectedUnidadeId } = useAuthStore();

  useEffect(() => {
    // Se o usuário selecionou uma unidade específica, filtrar automaticamente
    if (selectedUnidadeId !== null && selectedUnidadeId !== undefined) {
      setFiltroUnidade(selectedUnidadeId);
    }
  }, [selectedUnidadeId]);

  useEffect(() => {
    loadData();
  }, [filtroAno, filtroUnidade]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Se o usuário está logado como cliente de uma unidade específica,
      // forçar o filtro para aquela unidade
      const unidadeParaFiltrar = selectedUnidadeId !== null && selectedUnidadeId !== undefined 
        ? selectedUnidadeId 
        : filtroUnidade;
      
      const [prestacoesData, unidadesData] = await Promise.all([
        prestacoesApi.list(filtroAno, unidadeParaFiltrar),
        unidadesApi.list(),
      ]);
      console.log([prestacoesData, unidadesData]);
      setPrestacoes(prestacoesData);
      setUnidades(unidadesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMesNome = (mes: number) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses[mes - 1];
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      aberto: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      fechado: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    };
    return styles[status as keyof typeof styles] || styles.aberto;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Prestações de Contas
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gerencie e acompanhe suas prestações de contas
            </p>
          </div>
          <Link
            to="/prestacoes/nova"
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Nova Prestação</span>
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ano
              </label>
              <select
                value={filtroAno}
                onChange={(e) => setFiltroAno(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {[2024, 2025, 2026].map((ano) => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unidade
              </label>
              <select
                value={filtroUnidade || ''}
                onChange={(e) => setFiltroUnidade(e.target.value ? Number(e.target.value) : undefined)}
                disabled={selectedUnidadeId !== null && selectedUnidadeId !== undefined}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Todas as unidades</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Buscar
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Nome ou CPF do responsável..."
                  value={filtroBusca}
                  onChange={(e) => setFiltroBusca(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {prestacoes.filter(p => {
          if (!filtroBusca) return true;
          const busca = filtroBusca.toLowerCase();
          return (
            (p.responsavel_nome?.toLowerCase().includes(busca)) ||
            (p.responsavel_cpf?.toLowerCase().includes(busca))
          );
        }).length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nenhuma prestação encontrada
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filtroBusca ? 'Tente ajustar os filtros de busca' : 'Crie uma nova prestação de contas para começar'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {prestacoes.filter(p => {
              if (!filtroBusca) return true;
              const busca = filtroBusca.toLowerCase();
              return (
                (p.responsavel_nome?.toLowerCase().includes(busca)) ||
                (p.responsavel_cpf?.toLowerCase().includes(busca))
              );
            }).map((prestacao) => (
              <Link
                key={prestacao.id}
                to={`/prestacoes/${prestacao.id}`}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {prestacao?.unidade?.nome || 'Unidade sem nome'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(prestacao.status)}`}>
                        {prestacao.status.charAt(0).toUpperCase() + prestacao.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{getMesNome(prestacao.mes)} {prestacao.ano}</span>
                      </div>
                      {prestacao.responsavel_nome && (
                        <div>
                          <span className="font-medium">Responsável:</span> {prestacao.responsavel_nome}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_deferidos || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Deferidos</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400">
                        <Clock className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_pendentes || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Pendentes</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                        <XCircle className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_indeferidos || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Indeferidos</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
